import { useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { useOnceMutation } from './once-mutation';
import { getHttpClient } from '@vnetwork/margarita-core/http';

const foundationApi = getHttpClient('foundationApi');
const dataCentralApi = getHttpClient('dataCentralApi');

/**
 * Customer / organization profile — the CRM-style record behind the signed-in
 * account (company, contact details, status). Served by `/segment/info` on
 * margarita-foundation (falls back to @mgmt v3 when APP_FOUNDATION_API_URL is unset).
 */

export interface Customer {
  customer_id: number;
  customer_contact_id: number;
  company_id: number | null;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  contact_position: string;
  contact_industry: string;
  contact_source: string;
  contact_status: string;
  contact_language: string;
  contact_note?: string;
  contact_service_interestings: string[] | null;
  authentication_method: string;
  created_at: string;
  updated_at: string;
}

export interface ContactUpdate {
  contact_name: string;
  contact_phone: string;
  contact_address?: string;
  contact_position?: string;
  contact_industry?: string;
  contact_note?: string;
  contact_language?: string;
  contact_source?: string;
  contact_service_interestings?: string[];
}

export const customerKeys = {
  info: ['account', 'customer'] as const,
};

export function useCustomer() {
  return useQuery<Customer | null>({
    queryKey: customerKeys.info,
    queryFn: async () => {
      const res = await foundationApi.get('/segment/info');
      return (res.data?.data?.customer ?? null) as Customer | null;
    },
    staleTime: 60_000,
  });
}

/**
 * The signed-in account's editable contact record, read from the SAME source the
 * update writes to — DataCentral `GET /partner/auth/me` → result.user.contact.
 *
 * The Contact-information form must prefill from here, NOT from `useCustomer()`
 * (`/v2/segment/info`): that segment read-model is a separate store that does not
 * reflect a `PATCH /partner/auth/contact` write, so prefilling from it made saved
 * edits appear to "not persist" after reload. Reading + writing the same source
 * keeps the form consistent.
 */
export function useMyContactDetail() {
  return useQuery<ContactUpdate | null>({
    queryKey: ['dc', 'my-contact', 'detail'],
    retry: false,
    queryFn: async () => {
      const res = await dataCentralApi.get('/partner/auth/me');
      const c = res.data?.result?.user?.contact as
        | Record<string, unknown>
        | undefined;
      if (!c) return null;
      const str = (v: unknown) => (typeof v === 'string' ? v : '');
      return {
        contact_name: str(c.contact_name),
        contact_phone: str(c.contact_phone),
        contact_address: str(c.contact_address),
        contact_position: str(c.contact_position),
        contact_industry: str(c.contact_industry),
        contact_note: str(c.contact_note),
        contact_language: str(c.contact_language) || 'vi',
        contact_source: str(c.contact_source),
        contact_service_interestings: Array.isArray(c.contact_service_interestings)
          ? (c.contact_service_interestings as string[])
          : [],
      };
    },
  });
}

/** Update the signed-in account's CRM contact details (legacy ManageContact). */
export function useUpdateMyContact(
  options?: UseMutationOptions<unknown, unknown, ContactUpdate>,
) {
  const qc = useQueryClient();
  return useOnceMutation({
    mutationFn: async (payload: ContactUpdate) => {
      const res = await dataCentralApi.patch('/partner/auth/contact', payload);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      // Refresh the DataCentral contact reads (slim `['dc','my-contact']` +
      // the detail form source) and the segment snapshot, so every surface picks
      // up the change without a hard reload.
      qc.invalidateQueries({ queryKey: ['dc', 'my-contact'] });
      qc.invalidateQueries({ queryKey: customerKeys.info });
      options?.onSuccess?.(...args);
    },
  });
}
